/* eslint-disable max-lines */
import { LeaseState as LeaseStateObject } from '@common/license/types';
import { pick } from 'ramda';

export type EventObject = { name: string; params: Record<string, any> };

type ExtractEventName<T> = T extends EventObject ? T['name'] : T;

type ExtractEventParams<T> = T extends EventObject ? T['params'] : never;

export enum TrackingProperty {
  Type = 'CSV',
  ReportType = 'Report type',
  LeaseState = 'Lease state',
  NumberOfReportsDeleted = 'Number of reports deleted',
  EmailOfInvitedMember = 'Email of invited member',
  Location = 'Location',
  HasCodes = 'Has codes',
  HasComment = 'Has comment',
  HasDescription = 'Has description',
  SourceComponent = 'SourceComponent',
  EntityType = 'Entity type',
  Color = 'Color',
  VisualizationType = 'Visualization type',
  ProjectType = 'Project type',
  CsvSurveyImportSourceType = 'Csv import source type',
  NumberOfQuotationsCopied = 'Number of quotations copied',
  ConversationalAiSessionId = 'Conversational AI session ID',
  ConversationalAiMessageRating = 'Conversational AI message rating',
  AiIntentionalCodingSkippedOnStep = 'AI Intentional Coding skipped on step',
}

export enum SuperProperty {
  Language = 'Language',
  LicenseType = 'License type',
}

export enum UserProperty {
  ID = 'ID',
  Email = 'Email',
  FirstName = 'First name',
  LastName = 'Last name',
  RegistrationDate = 'Registration date',
}

/**
 * Events without parameters defined in the old format as enum only
 */

export enum GeneralEvent {
  OnboardingLastStepReached = 'Onboarding last step reached',
  ShortCutModalViewed = 'Shortcut modal viewed',
}

export enum TrackingEvent {
  TrialButtonOnOverlayClicked = 'Trial button on overlay clicked',
  UpgradeButtonOnOverlayClicked = 'Upgrade button on overlay clicked',
  DocumentOverviewViewed = 'Document overview viewed',
  EditModeDisabled = 'Edit mode disabled',
  EditModeEnabled = 'Edit mode enabled',
  CodingEnabled = 'Coding enabled',
  CodingDisabled = 'Coding disabled',
  AvatarsEnabled = 'Avatars enabled',
  AvatarsDisabled = 'Avatars disabled',
  CodeOverviewViewed = 'Code overview viewed',
}

export enum SourceEvent {
  ImportedPdfOriginal = 'Imported pdf original',
  ImportedPdfText = 'Imported pdf text',
  ImportedWordText = 'Imported word text',
  DocumentCreated = 'Document created',
  DocumentDuplicated = 'Document duplicated',
}

export enum LinkEvent {
  LicenseTrialLinkClicked = 'License trial link clicked',
  LicenseUpgradeLinkClicked = 'License upgrade link clicked',
}

export enum MemoEvent {
  MemoOverviewViewed = 'Memo overview viewed',
  MemoCreated = 'Memo created',
  MemoDuplicated = 'Memo duplicated',
}

export enum MemoDetailEvent {
  BlockMenuOpened = 'Memo block menu opened',
  ImageInserted = 'Memo image inserted',
  MemoViewed = 'Memo viewed',
  TextFormatChanged = 'Memo text formatting changed',
  TextInput = 'Memo text input',
  ContentPasted = 'Memo content pasted',
}

/**
 * Events defined in the new format as a union
 * If they have params, the definition is an object
 * Enum for the names is not strictly necessary
 */

export enum CodeEvent {
  CodeCreated = 'Code created',
  CodesDownloadedXlsx = 'Codes downloaded xlsx',
  CodesDownloadedQdc = 'Codes downloaded qdc',
  CodeDeleted = 'Code deleted',
  CodeDuplicated = 'Code duplicated',
}

export type CodeEvents =
  | CodeEvent.CodesDownloadedXlsx
  | CodeEvent.CodesDownloadedQdc
  | CodeEvent.CodeDeleted
  | CodeEvent.CodeDuplicated
  | {
      name: CodeEvent.CodeCreated;
      params: {
        [TrackingProperty.Color]: string;
        [TrackingProperty.HasComment]: boolean;
      };
    };

export enum OverviewEvent {
  OverviewFiltered = 'Overview filtered',
}

export type OverviewEvents = {
  name: OverviewEvent.OverviewFiltered;
  params: {
    [TrackingProperty.EntityType]: string;
  };
};

export enum SourceDetailEvent {
  BlockMenuOpened = 'Document block menu opened',
  DocumentViewed = 'Document viewed',
  ImageInserted = 'Document image inserted',
  PdfDocumentViewed = 'Pdf document viewed',
  SearchSubmitted = 'Search submitted',
  SearchTermAdded = 'Search term added',
  SearchResultsQuoted = 'Search results quoted',
  TextFormatChanged = 'Document text formatting changed',
  TextInput = 'Document text input',
  ContentPasted = 'Document content pasted',
  SentimentAnalysisInitiated = 'Sentiment analysis initiated',
  SentimentCodeSelected = 'Sentiment code selected',
  SentimentCodingCompleted = 'Sentiment coding completed',
  QuotationsCopied = 'Quotations copied into document',
}

export type SourceDetailEvents =
  | SourceDetailEvent.BlockMenuOpened
  | SourceDetailEvent.DocumentViewed
  | SourceDetailEvent.ImageInserted
  | SourceDetailEvent.PdfDocumentViewed
  | SourceDetailEvent.SearchSubmitted
  | SourceDetailEvent.SearchTermAdded
  | SourceDetailEvent.SearchResultsQuoted
  | SourceDetailEvent.TextFormatChanged
  | SourceDetailEvent.TextInput
  | SourceDetailEvent.ContentPasted
  | SourceDetailEvent.SentimentAnalysisInitiated
  | SourceDetailEvent.SentimentCodeSelected
  | SourceDetailEvent.SentimentCodingCompleted
  | {
      name: SourceDetailEvent.QuotationsCopied;
      params: {
        [TrackingProperty.NumberOfQuotationsCopied]: number;
      };
    };

export enum UserEvent {
  UserLoggedIn = 'User logged in',
  UserLoggedOut = 'User logged out',
  UserSettingsViewed = 'User settings viewed',
  ProfilePictureUpdated = 'Profile picture updated',
  ProfilePictureDeleted = 'Profile picture deleted',
  AccountDeleted = 'Account deleted',
  LanguageChanged = 'LanguageChanged',
}

export type UserEvents =
  | UserEvent.UserLoggedIn
  | UserEvent.UserLoggedOut
  | UserEvent.UserSettingsViewed
  | UserEvent.ProfilePictureUpdated
  | UserEvent.ProfilePictureDeleted
  | UserEvent.AccountDeleted
  | {
      name: UserEvent.LanguageChanged;
      params: {
        [SuperProperty.Language]: string;
      };
    };

export enum LicenseEvent {
  LeaseStateUpdated = 'Lease state updated',
}

export type LicenseEvents = {
  name: LicenseEvent;
  params: {
    [TrackingProperty.LeaseState]: LeaseStateObject;
  };
};

export enum ProjectEvent {
  ProjectCreated = 'Project created',
  ProjectDuplicated = 'Project duplicated',
  ProjectOverviewViewed = 'Project overview viewed',
  ProjectSettingsViewed = 'Project settings viewed',
  ProjectDeleted = 'Project deleted',
  ProjectExportedXlsx = 'Project exported xlsx',
  ProjectExportedDesktop = 'Project exported desktop',
  ProjectUpdated = 'Project updated',
  ProjectMemberInvited = 'Project member invited',
  DemoProjectOpened = 'Demo project opened',
  OwnershipTransferred = 'Project ownership transferred to team member',
}

export type ProjectEvents =
  | ProjectEvent.ProjectDuplicated
  | ProjectEvent.ProjectOverviewViewed
  | ProjectEvent.ProjectSettingsViewed
  | ProjectEvent.ProjectDeleted
  | ProjectEvent.ProjectExportedXlsx
  | ProjectEvent.ProjectExportedDesktop
  | ProjectEvent.DemoProjectOpened
  | ProjectEvent.OwnershipTransferred
  | {
      name: ProjectEvent.ProjectMemberInvited;
      params: { [TrackingProperty.EmailOfInvitedMember]: string };
    }
  | {
      name: ProjectEvent.ProjectCreated;
      params: { [TrackingProperty.HasDescription]: boolean; [TrackingProperty.Color]: string };
    }
  | {
      name: ProjectEvent.ProjectUpdated;
      params: { [TrackingProperty.HasDescription]: boolean; [TrackingProperty.Color]: string };
    };

export enum ProjectImportEvent {
  ProjectImportStarted = 'Project import started',
}

export type ProjectImportEvents = {
  name: ProjectImportEvent.ProjectImportStarted;
  params: {
    [TrackingProperty.ProjectType]: string;
  };
};

export enum QuotationEvent {
  QuotationOverviewViewed = 'Quotation overview viewed',
  QuotationChartDisabled = 'Quotation chart disabled',
  QuotationChartEnabled = 'Quotation chart enabled',
  QuotationsDownloaded = 'Quotations downloaded',
  QuotationCreated = 'Quotation created',
  CodeAddedToQuotation = 'Code added to quotation',
}

export type QuotationEvents =
  | QuotationEvent.QuotationOverviewViewed
  | QuotationEvent.QuotationChartDisabled
  | QuotationEvent.QuotationChartEnabled
  | QuotationEvent.QuotationsDownloaded
  | {
      name: QuotationEvent.QuotationCreated;
      params: {
        [TrackingProperty.HasCodes]: boolean;
        [TrackingProperty.HasComment]: boolean;
        [TrackingProperty.SourceComponent]: string;
      };
    }
  | {
      name: QuotationEvent.CodeAddedToQuotation;
      params: {
        [TrackingProperty.SourceComponent]: string;
      };
    };

export enum ReportEvent {
  ReportOverviewViewed = 'Report overview viewed',
  ReportChartEnabled = 'Report chart enabled',
  ReportChartDisabled = 'Report chart Disabled',
  ReportCreated = 'Report created',
  ReportQuotationsDownloaded = 'Report quotations downloaded',
  ReportDeleted = 'Report deleted',
  ReportNameOverlayOpened = 'Report name overlay opened',
  ReportVisualizationDisplayed = 'Report visualization displayed',
  ReportDuplicated = 'Report duplicated',
  ReportConceptAnalysisInitiated = 'Report Concept Analysis initiated',
  ReportConceptAnalysisWordSelected = 'Report Concept Analysis word selected in cloud',
  ReportConceptAnalysisBatchQuote = 'Report Concept Analysis batch quote',
  ReportViewed = 'Report viewed',
}

export type ReportEvents =
  | ReportEvent.ReportOverviewViewed
  | ReportEvent.ReportChartEnabled
  | ReportEvent.ReportChartDisabled
  | ReportEvent.ReportQuotationsDownloaded
  | ReportEvent.ReportNameOverlayOpened
  | ReportEvent.ReportDuplicated
  | ReportEvent.ReportConceptAnalysisInitiated
  | ReportEvent.ReportConceptAnalysisWordSelected
  | ReportEvent.ReportConceptAnalysisBatchQuote
  | {
      name: ReportEvent.ReportCreated;
      params: {
        [TrackingProperty.ReportType]: string;
      };
    }
  | {
      name: ReportEvent.ReportDeleted;
      params: {
        [TrackingProperty.NumberOfReportsDeleted]: number;
      };
    }
  | {
      name: ReportEvent.ReportVisualizationDisplayed;
      params: {
        [TrackingProperty.ReportType]: string;
        [TrackingProperty.VisualizationType]: string;
      };
    }
  | {
      name: ReportEvent.ReportViewed;
      params: {
        [TrackingProperty.ReportType]: string;
      };
    };

export enum CsvSurveyImportEvent {
  Selected = 'Csv file selected',
  Started = 'Csv survey data import started',
}

export type CsvSurveyImportEvents =
  | CsvSurveyImportEvent.Selected
  | {
      name: CsvSurveyImportEvent.Started;
      params: {
        [TrackingProperty.CsvSurveyImportSourceType]: 'rows' | 'columns';
      };
    };

export enum AiCodingEvent {
  AiCodingTryNow = 'AI Coding splash screen "Try now" clicked',
  AiCodingOpened = 'AI Coding opened',
  AiCodingStarted = 'AI Coding process started',
  AiCodingReview = 'AI Coding review started',
  AiCodingSummary = 'AI Coding summary displayed',
  AiCodingSaveAsReport = 'AI Coding result saved as report',
  AiCodingCanceled = 'AI Coding canceled',
}

export enum AiIntentionalCodingEvent {
  AiIntentionalCodingOpened = 'AI Intentional Coding opened',
  AiIntentionalCodingSkipped = 'AI Intentional Coding skipped',
  AiIntentionalCodingBack = 'AI Intentional Coding back',
  AiIntentionalCodingQuestionsGenerated = 'AI Intentional Coding questions generated',
  AiIntentionalCodingQuestionTextUpdate = 'AI Intentional Coding question text edited',
  AiIntentionalCodingQuestionCodeUpdate = 'AI Intentional Coding question code edited',
  AiIntentionalCodingCustomQuestionAdded = 'AI Intentional Coding custom question added',
  AiIntentionalCodingStarted = 'AI Intentional Coding process started (get code suggestions after questions)',
  AiIntentionalCodingReview = 'AI Intentional Coding review started',
  AiIntentionalCodingSummary = 'AI Intentional Coding summary displayed',
  AiIntentionalCodingSaveAsReport = 'AI Intentional Coding result saved as report',
  AiIntentionalCodingCanceled = 'AI Intentional Coding canceled',
}

export type AiIntentionalCodingEvents =
  | AiIntentionalCodingEvent.AiIntentionalCodingOpened
  | AiIntentionalCodingEvent.AiIntentionalCodingBack
  | AiIntentionalCodingEvent.AiIntentionalCodingQuestionsGenerated
  | AiIntentionalCodingEvent.AiIntentionalCodingQuestionTextUpdate
  | AiIntentionalCodingEvent.AiIntentionalCodingQuestionCodeUpdate
  | AiIntentionalCodingEvent.AiIntentionalCodingCustomQuestionAdded
  | AiIntentionalCodingEvent.AiIntentionalCodingStarted
  | AiIntentionalCodingEvent.AiIntentionalCodingReview
  | AiIntentionalCodingEvent.AiIntentionalCodingSummary
  | AiIntentionalCodingEvent.AiIntentionalCodingSaveAsReport
  | AiIntentionalCodingEvent.AiIntentionalCodingCanceled
  | {
      name: AiIntentionalCodingEvent.AiIntentionalCodingSkipped;
      params: {
        step: string;
      };
    };

export enum AiCodeSuggestionEvent {
  UsedSuggestedCode = 'Used suggested code',
}

export enum ConversationalAiEvent {
  ConversationalAiOpened = 'Conversational AI opened',
  ConversationalAiSessionStart = 'Conversational AI session started',
  ConversationalAiSessionClose = 'Conversational AI session closed',
  ConversationalAiUserMessageSent = 'Conversational AI user message sent',
  ConversationalAiMessageRated = 'Conversational AI message rated',
}

export type ConversationalAiEvents =
  | ConversationalAiEvent.ConversationalAiOpened
  | ConversationalAiEvent.ConversationalAiSessionStart
  | ConversationalAiEvent.ConversationalAiSessionClose
  | {
      name: ConversationalAiEvent.ConversationalAiUserMessageSent;
      params: {
        [TrackingProperty.ConversationalAiSessionId]: string;
        [UserProperty.ID]: string;
      };
    }
  | {
      name: ConversationalAiEvent.ConversationalAiMessageRated;
      params: {
        [TrackingProperty.ConversationalAiMessageRating]: 'negative' | 'positive';
      };
    };

export type ScholarEvents =
  | 'Paper search viewed'
  | 'Paper searched'
  | 'Paper search filtered'
  | 'Paper search PDF imported'
  | 'Paper search summary opened'
  | 'Paper search summary imported as memo'
  | 'Paper search paper detail opened'
  | {
      name: 'Citation style copied';
      params: { type: string };
    }
  | {
      name: 'Paper search view source link clicked';
      params: { url: string };
    }
  | {
      name: 'DOI link clicked';
      params: { url: string };
    }
  | {
      name: 'Semantic Scholar link clicked';
      params: { url: string };
    }
  | {
      name: 'PubMed link clicked';
      params: { url: string };
    }
  | {
      name: 'Paper search exported';
      params: { type: 'CSV' };
    };

export type GlobalSearchEvents = 'Global search document searched';

export type EditorDetailPasteEventType = SourceDetailEvent.ContentPasted | MemoDetailEvent.ContentPasted;
export type EditorDetailTextInputEventType = SourceDetailEvent.TextInput | MemoDetailEvent.TextInput;

/**
 * All Possible events
 */
export type AllEvents =
  // old type format
  | AiCodingEvent
  | GeneralEvent
  | LinkEvent
  | MemoDetailEvent
  | MemoEvent
  | SourceEvent
  | TrackingEvent
  // new type format
  | AiIntentionalCodingEvents
  | CodeEvents
  | ConversationalAiEvents
  | CsvSurveyImportEvents
  | GlobalSearchEvents
  | LicenseEvents
  | OverviewEvents
  | ProjectEvents
  | ProjectImportEvents
  | QuotationEvents
  | ReportEvents
  | ScholarEvents
  | SourceDetailEvents
  | UserEvents;

/**
 * Only the event names
 */
export type TrackingEventType = ExtractEventName<AllEvents>;

/**
 * Only the event params
 */
export type TrackingPropertyMapped = ExtractEventParams<AllEvents>;

export const eventCategoryMap: Map<string, 'crud' | 'editor'> = new Map();

[
  CodeEvent.CodeCreated,
  CodeEvent.CodeDeleted,
  CodeEvent.CodeDuplicated,
  SourceEvent.ImportedPdfOriginal,
  SourceEvent.ImportedPdfText,
  SourceEvent.ImportedWordText,
  SourceEvent.DocumentCreated,
  SourceEvent.DocumentDuplicated,
  MemoEvent.MemoCreated,
  MemoEvent.MemoDuplicated,
  ProjectEvent.ProjectCreated,
  ProjectEvent.ProjectDeleted,
  ProjectEvent.ProjectUpdated,
  ProjectEvent.ProjectMemberInvited,
  ProjectEvent.OwnershipTransferred,
  UserEvent.ProfilePictureUpdated,
  UserEvent.ProfilePictureDeleted,
  QuotationEvent.QuotationCreated,
  QuotationEvent.CodeAddedToQuotation,
  ReportEvent.ReportCreated,
  ReportEvent.ReportDeleted,
  ReportEvent.ReportDuplicated,
  SourceDetailEvent.SearchResultsQuoted,
  SourceDetailEvent.SentimentAnalysisInitiated,
  SourceDetailEvent.SentimentCodingCompleted,
  ReportEvent.ReportQuotationsDownloaded,
  CodeEvent.CodesDownloadedXlsx,
  CodeEvent.CodesDownloadedQdc,
  ProjectEvent.ProjectExportedXlsx,
  ProjectEvent.ProjectExportedDesktop,
  QuotationEvent.QuotationsDownloaded,
  UserEvent.UserSettingsViewed,
  UserEvent.AccountDeleted,
  UserEvent.LanguageChanged,
].forEach((event) => {
  eventCategoryMap.set(event, 'crud');
});

const editorEventKeys = ['BlockMenuOpened', 'ImageInserted', 'TextFormatChanged', 'TextInput', 'ContentPasted'];

[
  ...Object.values(pick(editorEventKeys, MemoEvent)),
  ...Object.values(pick(editorEventKeys, SourceDetailEvent)),
].forEach((event) => {
  eventCategoryMap.set(event, 'editor');
});
